import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthSessionService } from '../services/auth-session.service';
import { ApiRuntimeConfig } from '../services/api-runtime-config';

/**
 * The session backend marks a superseded session with `X-Ecam-Session` and a
 * `session_*` error code. An unlabelled live Dealer/Resolve 401 is treated as
 * an expired or rejected Stealth JWT.
 */
function isSessionConflict(err: HttpErrorResponse): boolean {
  if (err.status !== 401) {
    return false;
  }
  if (err.headers.get('X-Ecam-Session') === 'superseded') {
    return true;
  }
  const body = err.error;
  if (body && typeof body === 'object' && 'error' in body) {
    const code = String((body as { error?: string }).error ?? '');
    return code === 'session_superseded' || code === 'session_required';
  }
  return false;
}

function isDealerOrResolveUrl(url: string): boolean {
  return (
    url.includes('/api/dealer') ||
    url.includes('/api/resolve') ||
    url.includes('/dealer') ||
    url.includes('/resolve')
  );
}

function isSessionMissing(err: HttpErrorResponse): boolean {
  const body = err.error;
  return (
    err.status === 401 &&
    !!body &&
    typeof body === 'object' &&
    'error' in body &&
    String((body as { error?: string }).error ?? '') === 'session_missing'
  );
}

/**
 * Attaches the STS / session Stealth JWT as Bearer:
 * - `/api/dealer`, `/api/resolve`, `/dealer`, `/resolve`, and other `/api/` (except login)
 * - Live Dealer/Resolve with no session: fail closed (`401` `session_missing`) — do not send
 * - Mock mode still sends Dealer/Resolve URLs (EBM fixtures); the live gate does not apply
 * - `/Auth/`: never attach (token exchange is unauthenticated)
 * - `/dori/`: never attach (Dori uses its own shared APP_API_TOKEN on the request)
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.includes('/Auth/') || req.url.includes('/dori')) {
    return next(req);
  }

  const auth = inject(AuthSessionService);
  const liveDealerOrResolve =
    isDealerOrResolveUrl(req.url) && !inject(ApiRuntimeConfig).useMocks();
  const sessionToken = auth.accessToken()?.trim() ?? '';
  const isLogin = req.url.includes('/api/auth/login');
  if (!sessionToken) {
    if (liveDealerOrResolve) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            statusText: 'Unauthorized',
            url: req.url,
            error: { error: 'session_missing' },
          }),
      );
    }
    return next(req);
  }

  if (isLogin) {
    return next(req);
  }

  const needsBearer = isDealerOrResolveUrl(req.url) || req.url.includes('/api/');

  if (!needsBearer) {
    return next(req);
  }

  return next(
    req.clone({
      setHeaders: {
        Authorization: `Bearer ${sessionToken}`,
      },
    }),
  ).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && auth.isAuthenticated()) {
        if (isSessionConflict(err)) {
          auth.handleSessionSuperseded();
        } else if (err.status === 401 && liveDealerOrResolve && !isSessionMissing(err)) {
          auth.handleSessionExpired();
        }
      }
      return throwError(() => err);
    }),
  );
};
